<script setup lang="ts" generic="T extends Array<Record<string, any>>">
const windowScroll = useWindowScroll();
const device = useDevice();

const props = defineProps<{ items: T }>();
const parent = useTemplateRef('parent');
const imgs = useTemplateRef('imgs');
const offset = useElementBounding(parent);
const activeItem = ref<typeof props.items[number]>();
const bottomIndicate = 122;

const activeIndex = computed(() => 
    props.items.findIndex(e => e.title === activeItem.value?.title)
);

const startFixing = computed(() => {
    if (
        !windowScroll.y
        || !device.isDesktop.value
        || !imgs.value 
        || !offset.bottom.value 
        || !offset.top.value
    ) return;

    return offset.bottom.value > imgs.value.offsetHeight + bottomIndicate
        && offset.top.value < 110;
});

const endFixing = computed(() => {
    if (
        !windowScroll.y
        || !device.isDesktop.value
        || !imgs.value 
        || !offset.bottom.value
    ) return;

    return offset.bottom.value < imgs.value.offsetHeight + bottomIndicate;
});

onMounted(() => {
    if (!props.items) return;

    activeItem.value = props.items[0];
    props.items.forEach((item, index) => {
        const el = document.getElementById(item.title);
        if (!el) return;

        useIntersectionObserver(
            el,
            ([entry]) => {
                if (!entry?.isIntersecting) return;
                activeItem.value = props.items[index];
            },
            { rootMargin: '-20% 0px -50% 0px' }
        )
    });
})
</script>

<template>
    <div ref="parent" class="w-full">
        <div class="flex flex-col gap-75 items-center">
            <div
                v-for="item, index in props.items"
                :key="item.title"
                :id="item.title"
                class="w-full flex items-center gap-12 py-6 px-8 bg-transparent rounded-3xl mt-10"
                :class="index === props.items.length - 1 && 'pb-15'"
            >
                <div class="w-[45%]">
                    <h3>{{ item.title }}</h3>
                    <p class="text-body1 text-gray-400 mt-3">{{ item.desc }}</p>
                    <div class="w-full flex flex-col gap-9 mt-9">
                    <div
                        v-for="group in item.groups"
                        :key="group.title"
                        class="w-full"
                    >
                        <div class="w-full flex items-center gap-4">
                        <Icon name="solar:check-circle-bold" size="24" class="text-secondary-600" />
                        <span class="h4">{{ group.title }}</span>
                        </div>
                        <p class="text-body2 text-gray-400 mt-4 mr-10">{{ group.desc }}</p>
                    </div>
                    </div>
                </div>
            </div>
            <div 
                ref="imgs" 
                class="absolute top-60 inset-x-0"
                :class="[
                    startFixing && !endFixing && 'fixed top-32!',
                    endFixing && 'top-auto! bottom-8!'
                ]"
            >
                <div class="container flex justify-end">
                    <Transition name="fade" mode="out-in">
                        <div 
                            :key="activeItem?.img" 
                            class="w-[40%] bg-gray-50 border border-gray-100 rounded-3xl p-8 relative overflow-hidden"
                        >
                            <svg 
                                class="absolute w-45 h-35 blur-3xl rounded-full top-20 left-10"
                                :class="[
                                    activeIndex === 0 && 'bg-primary-200',
                                    activeIndex === 1 && 'bg-success-200',
                                    activeIndex === 2 && 'bg-warning-200',
                                ]"
                            ></svg>
                            <img
                                :src="activeItem?.img"
                                class="w-full relative z-1"
                                :alt="activeItem?.title ?? 'امکانات'"
                            />
                        </div>
                    </Transition>
                </div>   
            </div>
        </div>
    </div>
</template>